import type { CrowdexUIMessage } from '@crowdex/agent-contracts';
import { failInterruptedMessages } from '@crowdex/agent-contracts';
import { apiFetch } from '@crowdex/host-kit/renderer/http';
import type { PointerEvent as ReactPointerEvent } from 'react';
import type {
  ConversationDockPreview,
  ConversationDockSide,
} from '@/components/conversation-panel-layout';
import {
  dockLayoutPane,
  layoutHasPane,
  replaceLayoutPane,
} from '@/components/conversation-panel-layout';
import type { CachedConversation } from '@/lib/conversations/conversation-cache';
import { isCloudFolder, nextConversationWorkspace } from '@/lib/desktop-paths';
import { PREVIEW_MESSAGES } from '@/lib/desktop-preview-data';
import { resolveAgentForRuntime, resolvePersistedAgent } from '@/lib/desktop-shell/cloud-agents';
import {
  localWorkspaceRecord,
  selectWorktreeSourceRoot,
} from '@/lib/desktop-shell/conversation-worktree';
import { previewConversationTitle } from '@/lib/desktop-shell/message-derivations';
import {
  composerOf,
  holdTranscriptScroll,
  type PaneViewState,
  paneViewRestorable,
  readPaneView,
  restorePaneView,
} from '@/lib/desktop-shell/pane-view-state';
import type { DesktopShellStore, SyncedThread } from '@/lib/desktop-shell/store';
import { flushPendingComposerDraft } from '@/lib/desktop-shell/use-composer-draft';
import {
  sourceWorkspaceForThread,
  workspaceReplicaForThread,
} from '@/lib/desktop-shell/workspace-replicas';
import { reopenConversationWorktree } from '@/lib/desktop-shell/worktree-lifecycle';
import { WORKSPACE_KEY } from '@/lib/desktop-storage-keys';
import { errorMessage } from '@/lib/error-message';
import { getLocalConversation } from '@/lib/local-conversations';
import { isLocalMachine } from '@/lib/local-runtime';
import { latestSessionAgentName } from '@/lib/session-events';
import type { SidebarThread } from '@/lib/sidebar-threads';

export type ConversationNavigation = {
  recordNavigation: (id: string) => void;
  showNewConversation: (id: string) => void;
  resetConversation: (nextWorkspace?: string) => string;
  /**
   * Shows a conversation the server knows but the list does not hold yet (a new project's own
   * conversation has no message, so the listing leaves it out): its pane, empty, under its id.
   */
  openEmptyConversation: (id: string) => void;
  agentIdForStoredThread: (stored: SyncedThread | undefined) => string | null;
  selectThread: (
    selected: SidebarThread,
    options?: { preserveLayout?: boolean; record?: boolean; restoreCurrent?: boolean },
  ) => Promise<boolean>;
  loadConversation: (selected: SidebarThread) => Promise<CachedConversation | null>;
  openThreadInSplit: (
    selected: SidebarThread,
    targetId: string,
    side: ConversationDockSide,
  ) => Promise<void>;
  startThreadPointerDrag: (selected: SidebarThread, event: ReactPointerEvent<HTMLElement>) => void;
  focusSplitThread: (threadId: string) => void;
  activateSplitThread: (threadId: string) => Promise<boolean>;
  activateSidebarThread: (selected: SidebarThread) => void;
  navigateConversation: (direction: -1 | 1) => Promise<void>;
};

/** How long a split's swapped panes may take to be drawn again before they are left as they are. */
const SWAP_REDRAW_MS = 4000;

/**
 * Conversation navigation, thread selection, restoration, and split-pane
 * docking for the desktop shell. These closures previously lived inline in
 * `DesktopShell`; they are event-handler driven, so state is read from
 * `store.getState()` at call time (matching the sibling control factories).
 * Mutable refs the store does not own (the last-known cloud runtime, the
 * suppress-click guard, and the workspace container element used for scoped DOM
 * hit-testing) are injected, along with a getter for the derived sidebar thread
 * list that `navigateConversation` reads.
 */
export function createConversationNavigation({
  store,
  preview,
  cloudRuntimeRef,
  suppressThreadClickRef,
  workspaceContainerRef,
  selectionRequestRef,
  getSidebarThreads,
  replaceMessages,
  onSelectionSettled,
}: {
  store: DesktopShellStore;
  preview: boolean;
  cloudRuntimeRef: { current: unknown };
  suppressThreadClickRef: { current: boolean };
  workspaceContainerRef: { current: HTMLElement | null };
  selectionRequestRef: { current: number };
  getSidebarThreads: () => readonly SidebarThread[];
  /** Puts a conversation's transcript back, in its own chat. */
  replaceMessages?: (chatId: string, messages: CrowdexUIMessage[]) => void;
  onSelectionSettled?: (restored: boolean) => void;
}): ConversationNavigation {
  function clearMessageEditState(): void {
    const {
      chatId,
      editingMessageId,
      setPaneDrafts,
      setEditingMessageId,
      setEditingMessageContainer,
      setElementSelections,
      setResponseAnnotations,
    } = store.getState();
    flushPendingComposerDraft(store, chatId);
    setEditingMessageId(null);
    setEditingMessageContainer(null);
    setElementSelections([]);
    setResponseAnnotations([]);
    setPaneDrafts((current) => {
      if (editingMessageId === null) return current;
      if (!(chatId in current)) return current;
      const next = { ...current };
      delete next[chatId];
      return next;
    });
  }

  function recordNavigation(id: string): void {
    const { navigationIndex, setNavigationHistory, setNavigationIndex } = store.getState();
    setNavigationHistory((current) => {
      const next = [...current.slice(0, navigationIndex + 1), id];
      setNavigationIndex(next.length - 1);
      return next;
    });
  }

  function showNewConversation(id: string): void {
    selectionRequestRef.current += 1;
    clearMessageEditState();
    const {
      chatId,
      setInitialMessages,
      setGeneratedConversationTitle,
      setRestoredCollaboration,
      setRestoredModelId,
      setRestoredMessageCount,
      setMessageRevision,
      setConversationLayout,
      setChatId,
    } = store.getState();
    setInitialMessages(id, []);
    setGeneratedConversationTitle('New conversation');
    setRestoredCollaboration(null);
    setRestoredModelId(null);
    setRestoredMessageCount(null);
    setMessageRevision(0);
    setConversationLayout((current) => replaceLayoutPane(current, chatId, id));
    setChatId(id);
  }

  function resetConversation(nextWorkspace?: string): string {
    const nextId = crypto.randomUUID();
    startConversation(nextId, nextWorkspace);
    return nextId;
  }

  function openEmptyConversation(id: string): void {
    if (preview || id === store.getState().chatId) return;
    startConversation(id);
  }

  function startConversation(nextId: string, nextWorkspace?: string): void {
    const state = store.getState();
    const {
      native,
      executionMode,
      setWorkspace,
      setWorkspaceProvisionError,
      setExecutionMode,
      setWorktreeStartingState,
      setSplitThreadIds,
      setConversationLayout,
    } = state;
    const webOnly = native === false;
    const cloudAgentSelected = executionMode === 'cloud';
    // On this computer a new conversation runs here, whatever the one before ran on: only a
    // repository the cloud alone has (its folder is the cloud's) runs there.
    const cloud =
      native === true
        ? nextWorkspace !== undefined && isCloudFolder(nextWorkspace)
        : cloudAgentSelected;
    // A new conversation starts in the repository, not in the worktree of the one before.
    const repository = selectWorktreeSourceRoot(state);
    if (repository !== null) {
      localStorage.setItem(WORKSPACE_KEY, repository);
      setWorkspace(repository);
    }
    if (cloudAgentSelected && !cloud) {
      // Back from the cloud: this computer's agent, in the last folder opened here.
      const { agents, agentId, setAgentId, setRemoteExecutionTarget } = state;
      const command = resolvePersistedAgent(agents, agentId)?.command;
      const localAgent = resolveAgentForRuntime(agents, command, 'local');
      if (localAgent !== undefined) setAgentId(localAgent.id);
      setRemoteExecutionTarget(null);
      const remembered = localStorage.getItem(WORKSPACE_KEY) ?? state.personalWorkspaceRoot;
      if (nextWorkspace === undefined && remembered !== null)
        setWorkspace((current) =>
          current !== null && isCloudFolder(current) ? remembered : current,
        );
    }
    setExecutionMode(cloud ? 'cloud' : 'local');
    setWorktreeStartingState({ type: 'working-tree' });
    setWorkspaceProvisionError(null);
    setWorkspace((current) => nextConversationWorkspace(current, nextWorkspace, webOnly));
    setSplitThreadIds([]);
    setConversationLayout({ kind: 'pane', id: nextId });
    showNewConversation(nextId);
    recordNavigation(nextId);
  }

  function agentIdForStoredThread(stored: SyncedThread | undefined): string | null {
    if (stored === undefined) return null;
    const { syncedAgentConfigs, agents } = store.getState();
    const config = syncedAgentConfigs.find((entry) => entry.id === stored.agentConfigId);
    if (config === undefined) return null;
    return resolvePersistedAgent(agents, config.registryId ?? config.command)?.id ?? null;
  }

  /** A worktree made again: its folder is there now, for the sidebar and the next opening. */
  function markWorktreeAvailable(path: string): void {
    const { setSyncedWorkspaces, setSyncedThreads } = store.getState();
    setSyncedWorkspaces((current) =>
      current.map((entry) =>
        entry.rootPath === path ? { ...entry, availableOnDevice: true } : entry,
      ),
    );
    setSyncedThreads((current) =>
      current.map((entry) =>
        entry.worktree?.path === path
          ? { ...entry, worktree: { ...entry.worktree, available: true } }
          : entry,
      ),
    );
  }

  async function selectThread(
    selected: SidebarThread,
    options: { preserveLayout?: boolean; record?: boolean; restoreCurrent?: boolean } = {},
  ): Promise<boolean> {
    const state = store.getState();
    const {
      chatId,
      workspace: currentWorkspace,
      conversationCache,
      syncedThreads,
      syncedWorkspaces,
      agents,
      setExecutionMode,
      setWorkspace,
      setWorkspaceProvisionError,
      setGitStatus,
      setAgentId,
      setCloudRuntime,
      setInitialMessages,
      setGeneratedConversationTitle,
      setRestoredCollaboration,
      setRestoredModelId,
      setRestoredMessageCount,
      setMessageRevision,
      setConversationLayout,
      setChatId,
    } = state;
    if (preview || (selected.id === chatId && options.restoreCurrent !== true)) return false;
    const selectionRequest = ++selectionRequestRef.current;
    const selectionIsCurrent = () => selectionRequestRef.current === selectionRequest;
    const stored = syncedThreads.find((entry) => entry.id === selected.id);
    // On a hard reload, the shell briefly creates an empty cache entry for the
    // URL thread before bootstrap completes. It is only a placeholder, so the
    // persisted thread must win when restoring the already-active id.
    const cached =
      options.restoreCurrent === true && stored !== undefined
        ? undefined
        : conversationCache[selected.id];
    if (stored === undefined && cached === undefined) return false;
    const storedWorkspace =
      stored === undefined
        ? undefined
        : stored.runtime === 'cloud'
          ? sourceWorkspaceForThread(stored, syncedWorkspaces)
          : workspaceReplicaForThread(stored, syncedWorkspaces);
    let restoredMessages: CrowdexUIMessage[];
    let restoredRevision: number;
    if (cached !== undefined) {
      restoredMessages = cached.messages;
      restoredRevision = cached.messageRevision;
    } else {
      let data: { messages: CrowdexUIMessage[]; revision: number };
      try {
        if (stored?.runtime !== 'cloud') {
          const local = await getLocalConversation(selected.id);
          if (local === null) return false;
          data = {
            messages: local.messages.map(({ metadata, ...message }) => ({
              ...message,
              ...(metadata === null ? {} : { metadata }),
            })),
            revision: local.messageRevision,
          };
        } else {
          data = await apiFetch<{ messages: CrowdexUIMessage[]; revision: number }>(
            `/api/sync/messages?threadId=${encodeURIComponent(selected.id)}`,
            { credentials: 'include' },
          );
        }
      } catch {
        return false;
      }
      if (!selectionIsCurrent()) return false;
      restoredMessages = data.messages;
      restoredRevision = data.revision;
    }
    // A conversation of this machine opens in its folder as the server records it, listed in
    // the sidebar or not (its worktree cleaned up, its project removed).
    const nextWorkspace =
      cached?.workspaceRoot ??
      (stored?.runtime === 'cloud' ? undefined : stored?.workspaceRoot) ??
      storedWorkspace?.rootPath ??
      null;
    // Its worktree, cleaned up since: made again where it was, from the server's record. One
    // that cannot come back still opens its conversation: its transcript is there, and the
    // notice says why its folder is not.
    const worktree = stored?.runtime === 'cloud' ? null : (stored?.worktree ?? null);
    const worktreeGone =
      worktree !== null &&
      (worktree.available === false ||
        localWorkspaceRecord(syncedWorkspaces, worktree.path)?.availableOnDevice === false);
    let restoreFailure: string | null = null;
    if (worktree !== null && worktreeGone && nextWorkspace === worktree.path && isLocalMachine()) {
      try {
        await reopenConversationWorktree(selected.id, worktree);
        markWorktreeAvailable(worktree.path);
      } catch (error) {
        restoreFailure = `The worktree ${worktree.path} could not be restored: ${errorMessage(error)}`;
      }
      if (!selectionIsCurrent()) return false;
    }
    if (!selectionIsCurrent()) return false;
    clearMessageEditState();
    const restoredRuntime = cached?.runtime ?? stored?.runtime ?? 'local';
    setExecutionMode(restoredRuntime);
    if (nextWorkspace !== null) {
      setWorkspaceProvisionError(restoreFailure);
      if (nextWorkspace !== currentWorkspace) setGitStatus(null);
      if (restoredRuntime !== 'cloud') {
        localStorage.setItem(WORKSPACE_KEY, nextWorkspace);
      }
      setWorkspace(nextWorkspace);
    } else {
      if (currentWorkspace !== null) setGitStatus(null);
      cloudRuntimeRef.current = null;
      setCloudRuntime(null);
      setWorkspace(null);
      setWorkspaceProvisionError(null);
    }
    const restoredAgentId = cached?.agentId ?? agentIdForStoredThread(stored);
    const restoredAgentName = latestSessionAgentName(restoredMessages);
    const restoredAgent =
      (restoredAgentName === null
        ? undefined
        : agents.find((candidate) => candidate.name === restoredAgentName)) ??
      resolvePersistedAgent(agents, restoredAgentId);
    if (restoredAgent !== undefined) {
      setAgentId(restoredAgent.id);
      cloudRuntimeRef.current = null;
      setCloudRuntime(null);
    }
    const safeRestoredMessages = failInterruptedMessages(restoredMessages);
    setInitialMessages(selected.id, safeRestoredMessages);
    if (options.restoreCurrent === true) replaceMessages?.(selected.id, safeRestoredMessages);
    setGeneratedConversationTitle(cached?.title ?? stored?.title ?? selected.title);
    setRestoredCollaboration(cached?.collaboration ?? stored?.collaboration ?? null);
    setRestoredModelId(cached?.modelId ?? stored?.modelId ?? null);
    setRestoredMessageCount(
      cached?.restoredMessageCount ?? (cached === undefined ? restoredMessages.length : null),
    );
    setMessageRevision(restoredRevision);
    if (options.preserveLayout !== true) {
      setConversationLayout((current) =>
        layoutHasPane(current, selected.id)
          ? current
          : replaceLayoutPane(current, chatId, selected.id),
      );
    }
    setChatId(selected.id);
    if (options.record !== false) recordNavigation(selected.id);
    return true;
  }

  async function loadConversation(selected: SidebarThread): Promise<CachedConversation | null> {
    const {
      conversationCache,
      agents,
      agentId,
      workspace,
      syncedThreads,
      syncedWorkspaces,
      setConversationCache,
    } = store.getState();
    const cached = conversationCache[selected.id];
    if (cached !== undefined) return cached;
    const agent = resolvePersistedAgent(agents, agentId) ?? null;
    if (preview) {
      const conversation: CachedConversation = {
        id: selected.id,
        ...(agent === null ? {} : { agentId: agent.id }),
        title: selected.title,
        runtime: 'local',
        workspaceRoot: workspace,
        messages: PREVIEW_MESSAGES,
        updatedAt: Date.now(),
        restoredMessageCount: PREVIEW_MESSAGES.length,
        messageRevision: 0,
      };
      setConversationCache((current) => ({ ...current, [selected.id]: conversation }));
      return conversation;
    }
    const stored = syncedThreads.find((entry) => entry.id === selected.id);
    if (stored === undefined) return null;
    let data: { messages: CrowdexUIMessage[]; revision: number };
    try {
      if (stored.runtime !== 'cloud') {
        const local = await getLocalConversation(selected.id);
        if (local === null) return null;
        data = {
          messages: local.messages.map(({ metadata, ...message }) => ({
            ...message,
            ...(metadata === null ? {} : { metadata }),
          })),
          revision: local.messageRevision,
        };
      } else {
        data = await apiFetch<{ messages: CrowdexUIMessage[]; revision: number }>(
          `/api/sync/messages?threadId=${encodeURIComponent(selected.id)}`,
          { credentials: 'include' },
        );
      }
    } catch {
      return null;
    }
    const storedWorkspace =
      stored.runtime === 'cloud'
        ? sourceWorkspaceForThread(stored, syncedWorkspaces)
        : workspaceReplicaForThread(stored, syncedWorkspaces);
    const storedAgentId = agentIdForStoredThread(stored);
    const conversation: CachedConversation = {
      id: selected.id,
      ...(storedAgentId === null ? {} : { agentId: storedAgentId }),
      title: stored.title,
      runtime: stored.runtime,
      workspaceRoot: storedWorkspace?.rootPath ?? null,
      ...(stored.collaboration === undefined ? {} : { collaboration: stored.collaboration }),
      ...(stored.modelId === null || stored.modelId === undefined
        ? {}
        : { modelId: stored.modelId }),
      messages: failInterruptedMessages(data.messages),
      updatedAt: Date.parse(stored.updatedAt),
      restoredMessageCount: data.messages.length,
      messageRevision: data.revision,
    };
    setConversationCache((current) => ({ ...current, [selected.id]: conversation }));
    return conversation;
  }

  async function openThreadInSplit(
    selected: SidebarThread,
    targetId: string,
    side: ConversationDockSide,
  ): Promise<void> {
    const { chatId, splitThreadIds, setConversationLayout, setSplitThreadIds } = store.getState();
    if (selected.id === chatId || splitThreadIds.includes(selected.id)) return;
    if ((await loadConversation(selected)) === null) return;
    setConversationLayout((current) => dockLayoutPane(current, targetId, selected.id, side));
    setSplitThreadIds((current) => [...current, selected.id]);
  }

  function startThreadPointerDrag(
    selected: SidebarThread,
    event: ReactPointerEvent<HTMLElement>,
  ): void {
    const { chatId, setDraggedThread, setDockPreview } = store.getState();
    if (event.button !== 0) return;
    // The conversation shown cannot split beside itself.
    const splits = selected.id !== chatId;
    const pointerId = event.pointerId;
    const origin = { x: event.clientX, y: event.clientY };
    let dragging = false;
    let currentPreview: ConversationDockPreview | null = null;

    function previewAt(x: number, y: number): ConversationDockPreview | null {
      const container = workspaceContainerRef.current;
      const pane = document
        .elementFromPoint(x, y)
        ?.closest<HTMLElement>('[data-conversation-pane]');
      const targetId = pane?.dataset['conversationPane'];
      if (pane === null || pane === undefined || targetId === undefined) return null;
      if (container !== null && !container.contains(pane)) return null;
      const bounds = pane.getBoundingClientRect();
      const horizontal = (x - bounds.left) / bounds.width - 0.5;
      const vertical = (y - bounds.top) / bounds.height - 0.5;
      const side: ConversationDockSide =
        Math.abs(horizontal) > Math.abs(vertical)
          ? horizontal < 0
            ? 'left'
            : 'right'
          : vertical < 0
            ? 'top'
            : 'bottom';
      return {
        targetId,
        side,
        left: side === 'right' ? bounds.left + bounds.width / 2 : bounds.left,
        top: side === 'bottom' ? bounds.top + bounds.height / 2 : bounds.top,
        width: side === 'left' || side === 'right' ? bounds.width / 2 : bounds.width,
        height: side === 'top' || side === 'bottom' ? bounds.height / 2 : bounds.height,
      };
    }

    function move(pointerEvent: PointerEvent): void {
      if (pointerEvent.pointerId !== pointerId) return;
      if (
        !dragging &&
        Math.hypot(pointerEvent.clientX - origin.x, pointerEvent.clientY - origin.y) < 6
      ) {
        return;
      }
      if (!dragging) {
        dragging = true;
        suppressThreadClickRef.current = true;
        document.body.style.userSelect = 'none';
        document.body.style.cursor = 'grabbing';
      }
      pointerEvent.preventDefault();
      setDraggedThread({
        id: selected.id,
        title: selected.title,
        x: pointerEvent.clientX,
        y: pointerEvent.clientY,
      });
      currentPreview = splits ? previewAt(pointerEvent.clientX, pointerEvent.clientY) : null;
      setDockPreview(currentPreview);
    }

    function finish(pointerEvent: PointerEvent): void {
      if (pointerEvent.pointerId !== pointerId) return;
      document.removeEventListener('pointermove', move);
      document.removeEventListener('pointerup', finish);
      document.removeEventListener('pointercancel', finish);
      document.body.style.removeProperty('user-select');
      document.body.style.removeProperty('cursor');
      setDraggedThread(null);
      setDockPreview(null);
      if (dragging && currentPreview !== null) {
        void openThreadInSplit(selected, currentPreview.targetId, currentPreview.side);
      }
      window.setTimeout(() => {
        suppressThreadClickRef.current = false;
      }, 0);
    }

    document.addEventListener('pointermove', move, { passive: false });
    document.addEventListener('pointerup', finish);
    document.addEventListener('pointercancel', finish);
  }

  function paneElement(threadId: string): HTMLElement | null {
    return (workspaceContainerRef.current ?? document).querySelector<HTMLElement>(
      `[data-conversation-pane="${threadId}"]`,
    );
  }

  function focusPane(pane: HTMLElement): void {
    pane.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    composerOf(pane)?.focus({ preventScroll: true });
  }

  function focusSplitThread(threadId: string): void {
    const pane = paneElement(threadId);
    if (pane !== null) focusPane(pane);
  }

  /**
   * Made the active one, the pane and the one it swapped with are drawn again (the active one as
   * the window's conversation) on a coming render: once each is there, the active one's composer
   * gets the keys, and both get their caret and transcript scroll back.
   */
  function restoreSwappedPanes(
    activeId: string,
    views: ReadonlyMap<string, { before: HTMLElement | null; view: PaneViewState }>,
  ): void {
    const pending = new Map(views);
    // A busy window may take a while to draw them: a pane not drawn again by then keeps its own.
    const started = performance.now();
    const next = () => {
      const last = performance.now() - started >= SWAP_REDRAW_MS;
      // The active one first: the caret is put back in its focused composer.
      for (const id of [activeId, ...[...pending.keys()].filter((key) => key !== activeId)]) {
        const entry = pending.get(id);
        const pane = paneElement(id);
        if (entry === undefined || pane === null) continue;
        const drawn =
          pane !== entry.before &&
          composerOf(pane) !== null &&
          paneViewRestorable(pane, entry.view);
        if (!drawn && !last) continue;
        if (id === activeId) focusPane(pane);
        restorePaneView(pane, entry.view);
        pending.delete(id);
      }
      if (pending.size > 0 && !last) requestAnimationFrame(next);
    };
    requestAnimationFrame(next);
  }

  /**
   * Makes a conversation of the split the active one, in place: the side panel shows its own,
   * and the conversation active until now takes its pane beside it.
   */
  async function activateSplitThread(threadId: string): Promise<boolean> {
    const { chatId } = store.getState();
    // The preview's conversation is no saved one: it gets its preview copy to leave with.
    if (preview && !(chatId in store.getState().conversationCache))
      await loadConversation({
        id: chatId,
        title: previewConversationTitle(PREVIEW_MESSAGES, chatId),
        project: '',
        updatedAt: Date.now(),
      });
    const { splitThreadIds, conversationCache } = store.getState();
    const cached = conversationCache[threadId];
    // The active conversation leaves for a pane of the split, which shows its cached copy.
    if (
      !splitThreadIds.includes(threadId) ||
      cached === undefined ||
      !(chatId in conversationCache)
    )
      return false;
    const thread = getSidebarThreads().find((candidate) => candidate.id === threadId) ?? {
      id: threadId,
      title: cached.title,
      project: cached.workspaceRoot ?? '',
      updatedAt: cached.updatedAt,
    };
    // Both panes are drawn again: what the user saw of them is put back after.
    const views = new Map(
      [threadId, chatId].map((id) => {
        const before = paneElement(id);
        const view = readPaneView(before);
        holdTranscriptScroll(id, view);
        return [id, { before, view }] as const;
      }),
    );
    // The preview restores nothing: its conversations are its own data, named by their id.
    if (preview) {
      store.getState().setInitialMessages(threadId, cached.messages);
      store.getState().setChatId(threadId);
      recordNavigation(threadId);
    } else if (!(await selectThread(thread, { preserveLayout: true }))) return false;
    store
      .getState()
      .setSplitThreadIds((current) => [
        ...current.filter((id) => id !== threadId),
        ...(current.includes(chatId) ? [] : [chatId]),
      ]);
    restoreSwappedPanes(threadId, views);
    return true;
  }

  function activateSidebarThread(selected: SidebarThread): void {
    if (suppressThreadClickRef.current) return;
    const { splitThreadIds } = store.getState();
    if (splitThreadIds.includes(selected.id)) {
      void activateSplitThread(selected.id).then((activated) => {
        if (!activated) focusSplitThread(selected.id);
      });
    } else {
      void selectThread(selected).then((restored) => onSelectionSettled?.(restored));
    }
  }

  async function navigateConversation(direction: -1 | 1): Promise<void> {
    const { navigationIndex, navigationHistory, setNavigationIndex } = store.getState();
    const nextIndex = navigationIndex + direction;
    const nextId = navigationHistory[nextIndex];
    if (nextId === undefined) return;
    const thread = getSidebarThreads().find((candidate) => candidate.id === nextId);
    if (thread === undefined) {
      showNewConversation(nextId);
      setNavigationIndex(nextIndex);
      return;
    }
    const restored = await selectThread(thread, { record: false });
    onSelectionSettled?.(restored);
    if (restored) setNavigationIndex(nextIndex);
  }

  return {
    recordNavigation,
    showNewConversation,
    resetConversation,
    openEmptyConversation,
    agentIdForStoredThread,
    selectThread,
    loadConversation,
    openThreadInSplit,
    startThreadPointerDrag,
    focusSplitThread,
    activateSplitThread,
    activateSidebarThread,
    navigateConversation,
  };
}
